import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'
import * as api from '../api/client'
import { describeError } from '../api/errors'
import { reportRequestFailure, useServerStatus } from '../api/serverStatus'
import type { Project } from '../api/types'
import { navigate } from '../lib/router'
import { pushToast } from '../lib/toast'
import { formatBytes, formatDateTime, formatTime } from '../lib/format'
import { MAX_VIDEO_BYTES, VIDEO_ACCEPT, pickFile, probeVideoDuration } from '../lib/media'

type UploadState = { projectId: string; progress: number } | null

export function ProjectsPage(): JSX.Element {
  const status = useServerStatus()
  const [projects, setProjects] = useState<Project[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [creating, setCreating] = useState(false)
  const [upload, setUpload] = useState<UploadState>(null)
  const [importing, setImporting] = useState(false)

  const reload = useCallback(async (): Promise<void> => {
    try {
      const list = await api.listProjects()
      setProjects(list)
      setLoadError(null)
    } catch (err) {
      reportRequestFailure(err)
      setLoadError(describeError(err))
      setProjects((prev) => prev ?? [])
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  // Сервер поднялся — подтягиваем список автоматически.
  useEffect(() => {
    if (status.online === true && loadError !== null) void reload()
  }, [status.online, loadError, reload])

  const create = async (): Promise<void> => {
    const trimmed = title.trim()
    if (!trimmed) return
    setCreating(true)
    try {
      const project = await api.createProject(trimmed)
      setTitle('')
      setProjects((prev) => [project, ...(prev ?? [])])
      pushToast('success', 'Проект создан')
      navigate({ name: 'editor', projectId: project.id })
    } catch (err) {
      reportRequestFailure(err)
      pushToast('error', describeError(err))
    } finally {
      setCreating(false)
    }
  }

  const uploadVideo = async (project: Project): Promise<void> => {
    const file = await pickFile(VIDEO_ACCEPT)
    if (!file) return
    if (file.size > MAX_VIDEO_BYTES) {
      pushToast('error', `Файл ${formatBytes(file.size)} — лимит 512 МБ`)
      return
    }
    const duration = await probeVideoDuration(file)
    setUpload({ projectId: project.id, progress: 0 })
    try {
      const { promise } = api.uploadVideo(project.id, file, duration, (fraction) => {
        setUpload({ projectId: project.id, progress: fraction })
      })
      const updated = await promise
      setProjects((prev) => (prev ?? []).map((p) => (p.id === updated.id ? updated : p)))
      pushToast('success', 'Видео загружено')
    } catch (err) {
      reportRequestFailure(err)
      pushToast('error', describeError(err))
    } finally {
      setUpload(null)
    }
  }

  const remove = async (project: Project): Promise<void> => {
    if (!window.confirm(`Удалить проект «${project.title}» вместе с файлами?`)) return
    try {
      await api.deleteProject(project.id)
      setProjects((prev) => (prev ?? []).filter((p) => p.id !== project.id))
      pushToast('success', 'Проект удалён')
    } catch (err) {
      reportRequestFailure(err)
      pushToast('error', describeError(err))
    }
  }

  const rename = async (project: Project): Promise<void> => {
    const next = window.prompt('Новое название проекта', project.title)
    if (next === null) return
    const trimmed = next.trim()
    if (!trimmed || trimmed === project.title) return
    try {
      const updated = await api.patchProject(project.id, { title: trimmed })
      setProjects((prev) => (prev ?? []).map((p) => (p.id === updated.id ? updated : p)))
    } catch (err) {
      reportRequestFailure(err)
      pushToast('error', describeError(err))
    }
  }

  const importZip = async (): Promise<void> => {
    const file = await pickFile('.zip,application/zip')
    if (!file) return
    setImporting(true)
    try {
      const { promise } = api.importProject(file)
      const project = await promise
      setProjects((prev) => [project, ...(prev ?? [])])
      pushToast('success', 'Проект импортирован')
      navigate({ name: 'editor', projectId: project.id })
    } catch (err) {
      reportRequestFailure(err)
      pushToast('error', describeError(err))
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="page">
      <div className="row" style={{ marginBottom: 16 }}>
        <h1>Проекты</h1>
      <button type="button" className="ghost" onClick={() => navigate({ name: 'games' })}>
        ← К озвучкам
      </button>
        <span className="spacer" />
        <button type="button" onClick={() => void importZip()} disabled={importing}>
          {importing ? 'Импортирую…' : '⤒ Импорт ZIP'}
        </button>
      </div>

      <section className="panel" style={{ marginBottom: 16 }}>
        <header>
          <h2>Новый проект</h2>
        </header>
        <div className="row">
          <input
            type="text"
            placeholder="Например: Властелин колец — мост"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create()
            }}
          />
          <button
            type="button"
            className="primary"
            onClick={() => void create()}
            disabled={creating || !title.trim()}
          >
            Создать
          </button>
        </div>
      </section>

      {loadError ? (
        <div className="empty" style={{ marginBottom: 16 }}>
          <p>Не удалось загрузить список проектов.</p>
          <p className="faint">{loadError}</p>
          <button type="button" onClick={() => void reload()}>
            Повторить
          </button>
        </div>
      ) : null}

      {projects === null ? (
        <div className="empty">Загружаю…</div>
      ) : projects.length === 0 && !loadError ? (
        <div className="empty">Проектов пока нет. Создайте первый — это займёт секунду.</div>
      ) : (
        <div className="project-grid">
          {projects.map((project) => (
            <article key={project.id} className="project-card">
              <h3>{project.title}</h3>
              <div className="faint">
                {project.videoFile
                  ? `Видео: ${formatTime(project.videoDuration)}`
                  : 'Видео не загружено'}
              </div>
              <div className="faint">Создан {formatDateTime(project.createdAt)}</div>
              {upload && upload.projectId === project.id ? (
                <div className="progress">
                  <i style={{ width: `${Math.round(upload.progress * 100)}%` }} />
                </div>
              ) : null}
              <div className="row wrap" style={{ marginTop: 'auto' }}>
                <button
                  type="button"
                  className="primary"
                  onClick={() => navigate({ name: 'play', projectId: project.id, stage: 'setup' })}
                  title="Озвучивать по репликам, без таймлайна и слайдеров"
                >
                  ▶ Играть
                </button>
                <button
                  type="button"
                  onClick={() => navigate({ name: 'editor', projectId: project.id })}
                >
                  Редактор
                </button>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => void uploadVideo(project)}
                  disabled={upload !== null}
                >
                  {project.videoFile ? 'Заменить видео' : 'Загрузить видео'}
                </button>
                <span className="spacer" />
                <button type="button" className="ghost" onClick={() => void rename(project)}>
                  ✎
                </button>
                <button type="button" className="ghost" onClick={() => void remove(project)}>
                  🗑
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}
